import { HStack, Tabs, Text } from '@chakra-ui/react';
import CockpitDialog from '@components/cockpit/CockpitDialog';
import { CockpitEditorLayout } from '@components/cockpit/layouts/CockpitEditorLayout';
import ModifiedTag from '@components/common/form/ModifiedField';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { useCreatePilotMutation, useUpdatePilotMutation } from '@services/utm/pilot.api';
import { getPilotName } from '@utils/utm/pilot.utils';
import { pilotSchemaValidation } from '@validations/utm.schema';
import { useFormik } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { Pilot } from '@/types/utm/pilot.types';

import { Breadcrumb } from '../../Breadcrumb';
import { useUtmContext } from '../UtmManagement';
import PilotGeneralTab from './PilotGeneralTab';

const emptyPilot: Partial<Pilot> = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
};

export default function PilotFormContainer() {
  const { formatMessage } = useIntl();

  const { editedItem, setEditedItem } = useUtmContext();
  const pilot = (editedItem?.value as Partial<Pilot> | null | undefined) ?? emptyPilot;
  const activeSite = useAppSelector(selectActiveSite);
  const [open, setOpen] = useState(false);
  const [createPilot, { isLoading: isLoadingCreate }] = useCreatePilotMutation();
  const [updatePilot, { isLoading: isLoadingUpdate }] = useUpdatePilotMutation();
  const isNewPilot = pilot.id === undefined;
  const validationSchema = useValidation(pilotSchemaValidation);

  const formik = useFormik<Partial<Pilot>>({
    initialValues: pilot,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (isNewPilot) {
        createPilot({
          ...values,
          site: activeSite?.code ?? 'local',
          code: uuidv4(),
        } as Pilot)
          .unwrap()
          .then((pilot) => {
            setOpen(false);
            createToast(
              formatMessage({ id: 'components.utm.pilots.message.add' }, { name: getPilotName(pilot) }),
              ToastStatusEnum.SUCCESS,
            );
            setEditedItem(null);
          });
      } else {
        updatePilot({
          ...values,
        } as Pilot)
          .unwrap()
          .then((pilot) => {
            setOpen(false);
            createToast(
              formatMessage({ id: 'components.utm.pilots.message.update' }, { name: getPilotName(pilot) }),
              ToastStatusEnum.SUCCESS,
            );
            setEditedItem(null);
          });
      }
    },
  });

  const hasGeneralTabChanged = (() => {
    return (
      formik.initialValues.firstName !== formik.values.firstName ||
      formik.initialValues.lastName !== formik.values.lastName ||
      formik.initialValues.phoneNumber !== formik.values.phoneNumber ||
      formik.initialValues.email !== formik.values.email
    );
  })();

  const hasChanged = () => {
    return hasGeneralTabChanged;
  };

  return (
    <CockpitEditorLayout
      dialog={
        <CockpitDialog
          open={open}
          isLoading={isLoadingCreate || isLoadingUpdate}
          confirmLabel={formatMessage({ id: 'global.save' })}
          cancelLabel={formatMessage({ id: 'global.cancel' })}
          onConfirm={formik.submitForm}
          onCancel={() => setOpen(false)}
        >
          <Text color="neutral.100" width="100%" whiteSpace="pre-line">
            <FormattedMessage
              id={`components.utm.pilots.popup.${isNewPilot ? 'descriptionSaveNew' : 'descriptionSave'}`}
              values={{ name: getPilotName(formik.values as Pilot) }}
            />
          </Text>
        </CockpitDialog>
      }
      breadcrumbs={
        <Breadcrumb
          items={[
            {
              label: formatMessage({ id: 'components.utm.pilots.title' }),
              action: () => setEditedItem(null),
            },
            {
              label: isNewPilot
                ? formatMessage({
                    id: 'components.utm.pilots.newPilot',
                  })
                : getPilotName(formik.initialValues as Pilot),
            },
          ]}
        />
      }
      onBack={() => setEditedItem(null)}
      onSave={() => {
        if (!formik.isValid || !hasChanged()) {
          return;
        }
        if (editedItem!.value === null) {
          formik.submitForm();
        } else {
          setOpen(true);
        }
      }}
      isSaving={isLoadingCreate || isLoadingUpdate}
      isSaveDisabled={!formik.isValid || !hasChanged()}
    >
      <Tabs.Root orientation="vertical" defaultValue="general" size="md" lazyMount variant="vertical" userSelect="none">
        <Tabs.List>
          <Tabs.Trigger value="general">
            <HStack width="100%">
              <FormattedMessage id="components.utm.pilots.tab.general.title" />
              {hasGeneralTabChanged && <ModifiedTag />}
            </HStack>
          </Tabs.Trigger>
        </Tabs.List>
        <Tabs.ContentGroup backgroundColor="neutral.700">
          <Tabs.Content value="general">
            <PilotGeneralTab formik={formik} />
          </Tabs.Content>
        </Tabs.ContentGroup>
      </Tabs.Root>
    </CockpitEditorLayout>
  );
}
